import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// copyToClipboard tries the async Clipboard API first, then falls back to a
// hidden textarea + document.execCommand("copy") for non-secure contexts
// (HTTP origins other than localhost, where navigator.clipboard is undefined).
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy path
    }
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Backend serializes ST as a NUMERIC(20,9) decimal string; naive formatting
// with maximumFractionDigits:4 zeroes out sub-0.00005 values.
export function formatSTAmount(value: string | number): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!isFinite(n) || n === 0) return "0";
  const abs = Math.abs(n);
  if (abs >= 1) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  return n.toLocaleString(undefined, {
    maximumSignificantDigits: 6,
    minimumSignificantDigits: 1,
  });
}

// formatUSD reuses formatSTAmount's numeric formatting (ST and USD are 1:1)
// and only swaps the presentation prefix from "ST" to "$".
export function formatUSD(value: string | number): string {
  return "$" + formatSTAmount(value);
}

// formatCashUSD renders the "available cash" portion of st_balance — only the
// non-negative part. A negative st_balance means the team is using its credit
// line; that portion is shown separately via formatCreditUsedUSD, so the cash
// figure itself is never negative.
export function formatCashUSD(st: string | number): string {
  const n = typeof st === "string" ? Number(st) : st;
  return formatUSD(isFinite(n) ? Math.max(0, n) : 0);
}

// formatCreditUsedUSD renders how much of the credit line is currently used —
// the magnitude of the negative part of st_balance (0 when st_balance >= 0).
export function formatCreditUsedUSD(st: string | number): string {
  const n = typeof st === "string" ? Number(st) : st;
  return formatUSD(isFinite(n) ? Math.max(0, -n) : 0);
}

// formatSignedUSD renders a delta (e.g. WalletTransaction.st_delta) with an
// explicit leading sign — "+$12.34" / "-$5.00" / "$0" — so recharges and
// deductions are visually distinguishable in transaction lists without
// relying on toLocaleString's bare "-" (and no sign at all for positives).
export function formatSignedUSD(value: string | number): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!isFinite(n) || n === 0) return formatUSD(0);
  const sign = n > 0 ? "+" : "-";
  return sign + formatUSD(Math.abs(n));
}

// formatDiscount renders a TeamBillingConfig.discount_rate decimal string
// (e.g. "0.8") as a human-friendly discount, e.g. zh "8 折 / 80%" / en "80%".
// A rate >= 1 (no discount) — or malformed/non-finite input — renders via
// the "billing.noDiscount" translation key instead of a bogus percentage.
export function formatDiscount(
  rate: string | number,
  language: "zh" | "en",
  t: (key: string) => string,
): string {
  const n = typeof rate === "string" ? Number(rate) : rate;
  if (!isFinite(n) || n >= 1) return t("billing.noDiscount");
  const percent = Math.round(n * 100);
  if (language !== "zh") return `${percent}%`;
  const zhe = Math.round(n * 100) / 10; // e.g. 0.85 -> 8.5
  const zheStr = Number.isInteger(zhe) ? String(zhe) : zhe.toFixed(1);
  return `${zheStr}${t("billing.discountUnit")} / ${percent}%`;
}

// formatBillingCycle renders a TeamBillingConfig.billing_cycle enum via
// translations.ts copy. Unknown values fall back to monthly to match the
// backend default (see BillingConfigService.Get).
export function formatBillingCycle(
  cycle: string,
  t: (key: string) => string,
): string {
  return cycle === "weekly" ? t("billing.cycleWeekly") : t("billing.cycleMonthly");
}

export function exportToCsv(filename: string, headers: string[], rows: string[][]) {
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [
    headers.map(escape).join(","),
    ...rows.map((row) => row.map(escape).join(",")),
  ];
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
